import { ReplaySubject } from 'rxjs';
import axios from 'axios';
import { socketService } from './socket.service';
import { userService } from './user.service';

export interface FGCEvent {
  id: number;
  current_total: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface FGCDonation {
  id: number;
  donor_name: string;
  amount: number;
  message: string;
  created_at: string;
}

class FGCService {
  private eventSubject = new ReplaySubject<FGCEvent>(1);
  private donationsSubject = new ReplaySubject<FGCDonation[]>(1);

  constructor() {
    this.initSocket();
  }

  private async initSocket() {
    await socketService.ready;

    socketService.on('fgc:donation', () => {
      this.fetchEvent();
      this.fetchDonations();
    });

    socketService.on('fgc:reset', () => {
      this.fetchEvent();
      this.fetchDonations();
    });

    socketService.on('fgc:total-updated', () => {
      this.fetchEvent();
    });

    socketService.on('fgc:donation-removed', () => {
      this.fetchEvent();
      this.fetchDonations();
    });
  }

  getEvent() {
    return this.eventSubject.asObservable();
  }

  getDonations() {
    return this.donationsSubject.asObservable();
  }

  async fetchEvent() {
    try {
      const resp = await axios.get('/api/fgc/status');
      this.eventSubject.next(resp.data);
    } catch (e) {
      console.error('Failed to fetch FGC event', e);
    }
  }

  async fetchDonations() {
    try {
      const resp = await axios.get('/api/fgc/donations');
      this.donationsSubject.next(resp.data);
    } catch (e) {
      console.error('Failed to fetch donations', e);
    }
  }

  async recordDonation(orderId: string, donorName: string, amount: number, message: string) {
    const resp = await axios.post('/api/fgc/donate', {
      orderId,
      donorName,
      amount,
      message
    });
    return resp.data;
  }

  async resetEvent() {
    const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
    const resp = await axios.post('/api/fgc/admin/reset', {}, { headers: authHeaders });
    return resp.data;
  }

  async updateTotal(total: number) {
    const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
    const resp = await axios.post('/api/fgc/admin/update-total', { total }, { headers: authHeaders });
    return resp.data;
  }

  async updateDonation(id: number, data: { donor_name: string; amount: number; message: string }) {
    const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
    const resp = await axios.patch(`/api/fgc/admin/donations/${id}`, data, { headers: authHeaders });
    return resp.data;
  }

  async deleteDonation(id: number) {
    const authHeaders = { 'Authorization': 'Bearer ' + userService.jwt };
    const resp = await axios.delete(`/api/fgc/admin/donations/${id}`, { headers: authHeaders });
    return resp.data;
  }
}

export const fgcService = new FGCService();
